import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { EventRowNFL } from 'src/domains/sportsbook/shared/Components';
import { HomepageWidgetNFLHeader } from './HomepageWidgetNFLHeader';
import { HeroWidgetModelType } from 'src/api/config/cms_new/hero_widget/get_hero_widget_active';
import { HeroWidgetItemState } from 'src/domains/layouts/webview/components/heroEvents/HeroWidgetItem.state';
import { HeroWidgetBanner } from 'src/domains/layouts/webview/components/heroEvents/HeroWidgetBanner';
import { EventListGroupEventItemType } from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';
import { HeroWidgetMainBanner } from 'src/domains/layouts/webview/components/heroEvents/HeroWidgetMainBanner';
import { useCommon } from 'src/domains/common/Common';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { BreakpointsState } from 'src/domains/layouts/state/breakpointsState/BreakpointsState';
import { NFLEventRow6PackState } from 'src/domains/sportsbook/webview/components/eventsList/eventRowNFL/EventRowNFL.state';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { HomepageHeroWidgetNFLOutrights } from './HomepageWidgetNFLOutrights';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { EventRowUnified } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventRowUnified';
import { EventHeader } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventGroupHeaderUnified';
import { getMarketAndSelections } from 'src/domains/sportsbook/state/eventsListState/eventsGroup/getMarketAndSelections';
import { SELECTIONS_LABELS } from 'src/domains/sportsbook/state/eventsListState/unifiedSportEventsListState/UnifiedEventsListMarkets';
import { EventId } from 'src_common/common/websocket2/id/WebsocketId';

interface HomepageHeroWidgetNFLPropsType {
    heroWidgetItem: HeroWidgetModelType;
    smallEvent: EventListGroupEventItemType;
}

const renderEventUnified = (
    event_id: EventId,
    smallEvent: EventListGroupEventItemType,
    languagesState: LanguagesState
) => {
    const event = event_id.getEventModel();
    const competitionId = event?.competition2 ?? null;
    const competitionName = competitionId?.getModel()?.name ?? 'NFL';
    const possibleMarketAndSelectionInList = getMarketAndSelections([smallEvent]);
    const selectionsLabels = SELECTIONS_LABELS(languagesState.getTranslation);
    const selections = possibleMarketAndSelectionInList.headers.map(
        (x) => selectionsLabels[`americanfootball:${x}`] ?? selectionsLabels[x] ?? x
    );

    return (
        <>
            <EventHeader
                selections={selections}
                label={competitionName}
                showCompetitonLabel
                sportId='americanfootball'
            />
            <EventRowUnified
                key={`event_${event_id}`}
                sportId={'americanfootball'}
                smallEvent={smallEvent}
                selectionsHeaders={['H', 'A']}
                competitionId={competitionId}
            />
        </>
    );
};

export const HomepageHeroWidgetNFL = observer(
    'HomepageHeroWidgetNFL',
    ({ heroWidgetItem, smallEvent }: HomepageHeroWidgetNFLPropsType): JSX.Element | null => {
        const { event_id, is_active_promo, title, subtitle, id, tc_text, tc_title, header_bg_color } = heroWidgetItem;
        const common = useCommon();
        const starRouter = StarRouter.get(common);
        const breakpointsState = BreakpointsState.get(common);
        const languagesState = LanguagesState.get(common);
        const { unifiedSportEventsTable } = ConfigComponents.get(common).unifiedComponentConfig;

        const [eventRow6PackState] = useState(() => new NFLEventRow6PackState(event_id, languagesState));
        const [heroWidgetItemState] = useState(() => new HeroWidgetItemState(starRouter, heroWidgetItem));
        const { headerImages, promoImages, marketModel, handleRedirectTEventPage } = heroWidgetItemState;

        if (marketModel !== null) {
            return (
                <React.Fragment key={marketModel.id}>
                    <>
                        <HeroWidgetMainBanner
                            images={headerImages}
                            title={title}
                            subtitle={subtitle}
                            bgColor={header_bg_color}
                            onClick={handleRedirectTEventPage}
                        />
                        {eventRow6PackState.event?.eventType === 'outrights' ? (
                            <HomepageHeroWidgetNFLOutrights smallEvent={smallEvent} />
                        ) : unifiedSportEventsTable ? (
                            renderEventUnified(event_id, smallEvent, languagesState)
                        ) : (
                            <>
                                {breakpointsState.desktop.isBiggerOrEq === true ? <HomepageWidgetNFLHeader /> : null}
                                <EventRowNFL
                                    smallEvent={smallEvent}
                                    eventId={event_id}
                                />
                            </>
                        )}
                    </>
                    {is_active_promo ? (
                        <HeroWidgetBanner
                            withMargin={false}
                            banners={promoImages}
                            slug={`${id}-hero-promo`}
                            content={tc_text ?? ''}
                            title={tc_title ?? ''}
                        />
                    ) : null}
                </React.Fragment>
            );
        }
        return null;
    }
);
